<script>
  // The mapping of one source, and nothing else (docs/ref-belonging-plan.md,
  // step 7.1): what each column of its file is, how it is labelled and
  // transformed, and what it publishes. Cubes are made on the dataset page.
  //
  // The table is a review surface, not a form. It reads left to right as the
  // pipeline — what the column holds, then the three things you can decide
  // about it, then what it publishes — and every control lives in the
  // per-column editor. The recurring task is checking an updated export
  // against the mapping, so the file's values and the result sit in one row.
  //
  // Save writes mapping.json only. The records already in the Pod were read
  // with the old mapping; the next ingest reads them again.
  import { onMount } from 'svelte';
  import Panel from '../components/Panel.svelte';
  import Section from '../components/Section.svelte';
  import Button from '../components/Button.svelte';
  import Alert from '../components/Alert.svelte';
  import Drawer from '../components/Drawer.svelte';
  import HelpDrawer from '../components/HelpDrawer.svelte';
  import ColumnEditor from '../components/ColumnEditor.svelte';
  import { HELP } from '../lib/help.js';
  import { getDisclosure, getStoredSource, saveMapping, sourceRemoval, deleteSource } from '../lib/api.js';
  import { nav, go, guardLeaving } from '../lib/nav.svelte.js';
  import { mayVisit } from '../lib/session.svelte.js';
  import {
    TRANSFORM_LABELS, transformSteps, overridesFor, sortBySourceOrder, prepareMappingDraft,
    parseAndProfileCsv, traceColumn, reconcile, recordSummary, columnProblems,
  } from '../../../lib/disclosure_core.js';
  import { resolveDatasets, recordContainer } from '../../../lib/datasets.js';

  const sourceId = nav.params.id;
  let source = $state(null);
  let mapping = $state(null);
  // What was last saved, to tell whether anything changed since.
  let saved = $state('');
  let error = $state(null);
  let saving = $state(false);
  let refused = $state(null);
  let done = $state(false);
  // The file being looked at: the source's own, or another export tried here.
  // Neither is uploaded or changes the mapping.
  let file = $state({ name: null, stored: false, rows: [], headers: [], profiles: [] });
  let rowIndex = $state(1);
  // Which column is open, and which of its three facets.
  let editing = $state(null);
  let facet = $state(null);

  const dirty = $derived(Boolean(mapping) && JSON.stringify(mapping) !== saved);
  const ask = () => !dirty || window.confirm(`Leave without saving the changes to the mapping of ${source?.connector?.sourceLabel ?? sourceId}?`);
  const unload = (event) => { if (dirty) { event.preventDefault(); event.returnValue = ''; } };

  onMount(() => {
    load();
    window.addEventListener('beforeunload', unload);
    const release = guardLeaving(ask);
    return () => { release(); window.removeEventListener('beforeunload', unload); };
  });

  async function load() {
    try {
      const read = await getDisclosure(sourceId);
      mapping = prepareMappingDraft(read.mapping);
      source = read;
      saved = JSON.stringify(mapping);
    } catch (e) { error = e.message; return; }
    // Reading the stored file back is the steward's alone.
    getStoredSource(sourceId).then(({ file: stored }) => {
      if (stored && !file.name) open(stored.fileName, stored.csvText, true);
    }).catch(() => {});
  }

  function open(name, text, stored = false) {
    const parsed = parseAndProfileCsv(text);
    file = { name, stored, rows: parsed.rows, headers: parsed.headers, profiles: parsed.profiles };
    rowIndex = 1;
  }
  async function choose(e) {
    const chosen = e.target.files?.[0];
    if (!chosen) return;
    try { error = null; open(chosen.name, await chosen.text()); } catch (x) { error = x.message; }
  }

  const changed = () => { done = false; refused = null; };
  const authored = $derived((mapping?.columns ?? []).filter((c) => c.record !== 'shared'));
  const housekeeping = $derived((mapping?.columns ?? []).filter((c) => c.record === 'shared'));
  const hasRows = $derived(file.rows.length > 0);
  const row = $derived(hasRows ? file.rows[Math.min(Math.max(rowIndex, 1), file.rows.length) - 1] : null);
  const state = $derived(reconcile(mapping, file.headers));
  const records = $derived(recordSummary(mapping));

  // Grouped by record class, which follows from the term. Columns with no term
  // yet have no record class either, and gather in their own group.
  const UNLABELLED = 'Not yet labelled';
  const groups = $derived.by(() => {
    const byRecord = new Map(Object.keys(mapping?.records ?? {}).map((id) => [id, []]));
    for (const column of authored) {
      const key = byRecord.has(column.record) ? column.record : UNLABELLED;
      if (!byRecord.has(key)) byRecord.set(key, []);
      byRecord.get(key).push(column);
    }
    return [...byRecord].filter(([, columns]) => columns.length);
  });
  const declared = $derived(source?.datasets?.datasets ?? []);
  const datasetOf = (recordClass) => declared.find((d) => d.recordClasses.includes(recordClass)) ?? null;
  // Where its records are stored: its dataset's container (lib/datasets.js).
  const resolved = $derived(resolveDatasets(source?.datasets, mapping));
  const containerOf = (recordClass) => {
    const dataset = resolved.find((d) => d.recordClasses.includes(recordClass));
    return dataset ? recordContainer(dataset.id) : '—';
  };

  const profileOf = (header) => file.profiles.find((p) => p.name === header) ?? null;
  const isMissing = (header) => file.headers.length > 0 && !file.headers.includes(header);
  const raw = (column) => (row ? String(row[column.source] ?? '') : '');
  const published = (column) => {
    if (!row) return '';
    const trace = traceColumn(column, row);
    return trace.error ? '⚠ ' + trace.error : trace.values.join(' · ');
  };
  const chainLabel = (column) => transformSteps(column.transform).map((s) => TRANSFORM_LABELS[s.fn] ?? s.fn).join(' → ');
  const problems = (column) => columnProblems(column, file.headers);
  const incomplete = $derived(authored.filter((c) => problems(c).length));
  // What has to be settled before saving: every column of the loaded file
  // accounted for, and every kept column answered.
  const blocked = $derived(state.unaccounted.length ? 'Every column in the file needs a decision first.'
    : incomplete.length ? 'Finish the columns that need attention first.' : null);

  const edit = (column, which) => { editing = column; facet = which; };

  function dropNew(header) {
    if (!mapping.drop.includes(header)) mapping.drop.push(header);
    sortBySourceOrder(mapping, file.headers);
    changed();
  }
  function keepColumn(header) {
    mapping.drop.splice(mapping.drop.indexOf(header), 1);
    // Nothing is guessed: a restored column has no term until one is chosen,
    // and so no record class either.
    mapping.columns.push({ source: header, record: null, target: '', term: '', transform: { fn: 'direct' },
      identifies: 'no', sensitivity: 'personal', treatment: 'isolate' });
    sortBySourceOrder(mapping, file.headers);
    changed();
    // Open the stored (proxied) object, or the drawer's edits are lost.
    edit(mapping.columns.find((c) => c.source === header && c.record !== 'shared'), 'term');
  }
  function dropColumn(column) {
    mapping.columns.splice(mapping.columns.indexOf(column), 1);
    if (!mapping.drop.includes(column.source)) mapping.drop.push(column.source);
    sortBySourceOrder(mapping, file.headers);
    editing = null;
    changed();
  }
  // A record class with no columns left is a leftover, and can be cleared.
  function removeRecord(id) {
    if (records.find((r) => r.id === id)?.columns.length) return;
    delete mapping.records[id];
    changed();
  }

  async function save() {
    saving = true; refused = null; done = false;
    try {
      const result = await saveMapping(sourceId, $state.snapshot(mapping));
      mapping = prepareMappingDraft(result.mapping);
      saved = JSON.stringify(mapping);
      done = true;
    } catch (e) {
      const details = e.details ?? {};
      refused = { message: e.message, reasons: [...(details.uses ?? []), ...(details.mapping ?? []), ...(details.aggregations ?? [])] };
    } finally { saving = false; }
  }
  // Deleting the source, to correct a mistake: first what would go (or what
  // stands in the way), then the steward types its name to confirm.
  const label = $derived(source?.connector?.sourceLabel ?? sourceId);
  let removal = $state(null), typed = $state(''), deleting = $state(false), deleteProblem = $state(null);
  async function askDelete() {
    deleteProblem = null; typed = '';
    try { removal = await sourceRemoval(sourceId); } catch (e) { deleteProblem = e.message; }
  }
  async function confirmDelete() {
    deleting = true; deleteProblem = null;
    try { await deleteSource(sourceId); saved = JSON.stringify(mapping); go('sources'); }
    catch (e) { deleteProblem = e.message; } finally { deleting = false; }
  }
  const several = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

  function revert() {
    if (!window.confirm('Undo every change since the mapping was last saved?')) return;
    mapping = JSON.parse(saved);
    editing = null;
    changed();
  }
</script>

<HelpDrawer {...HELP['source/mapping']} />
<h1 class="wf2-title wf2-title-level-1">Mapping of {source?.connector?.sourceLabel ?? sourceId}</h1>
<p class="wf2-subtitle">
  What each column of this source's file is, and what it publishes. The record class and how a column
  may be published follow from the shared term; change them only where you disagree.
</p>

{#if error && !mapping}
  <Alert title="Could not open the mapping" variant="danger"><p>{error}</p></Alert>
{:else if !mapping}
  <div class="state"><span class="spinner"></span>Opening the mapping…</div>
{:else}
  <Section title="The file">
    <Panel>
      {#if file.name}
        <p class="wf2-text"><strong>{file.name}</strong> · {file.rows.length} rows · {file.headers.length} columns
          {#if file.stored}<span class="wf2-caption">(the source's latest file)</span>{/if}</p>
      {:else}
        <p class="wf2-hint">
          No file to look at. You can still read and change the mapping; load an export to see what
          the transforms make of its values.
        </p>
      {/if}
      <label class="wf2-field">{file.name ? 'Try another export' : 'Load a CSV'}
        <input class="wf2-control" type="file" accept=".csv,text/csv" onchange={choose} /></label>
      <p class="wf2-caption">The file is read in this browser tab. It is not uploaded and does not change the mapping.</p>
    </Panel>
  </Section>

  {#if error}<Alert title="Could not read CSV" variant="danger"><p>{error}</p></Alert>{/if}

  {#if state.unaccounted.length}
    <Alert title="{state.unaccounted.length} {state.unaccounted.length === 1 ? 'column is' : 'columns are'} in the file but not in the mapping" variant="warning">
      <ul>
        {#each state.unaccounted as header}
          <li>
            {header} — {profileOf(header)?.distinct ?? 0} different values
            <Button variant="ghost" onclick={() => dropNew(header)}>Don't publish it</Button>
            <Button variant="ghost" onclick={() => keepColumn(header)}>Label it</Button>
          </li>
        {/each}
      </ul>
    </Alert>
  {/if}

  {#if state.missing.length}
    <Alert title="{state.missing.length} {state.missing.length === 1 ? 'column is' : 'columns are'} missing from the loaded file" variant="warning">
      <p>
        The mapping expects {state.missing.map((s) => `“${s}”`).join(', ')}, which this file does not
        have. Left as is they publish nothing at all. Rename them in the export, or stop publishing them.
      </p>
    </Alert>
  {/if}

  {#if incomplete.length}
    <Alert title="{incomplete.length} {incomplete.length === 1 ? 'column needs' : 'columns need'} attention" variant="warning">
      <ul>
        {#each incomplete as column}
          <li>
            <button class="wf2-link" onclick={() => edit(column, column.term ? 'transform' : 'term')}>{column.source}</button>
            — {problems(column)[0]}
          </li>
        {/each}
      </ul>
    </Alert>
  {/if}

  <Panel>
    {#if hasRows}
      <div class="wf2-row" style="gap:12px;align-items:center">
        <label class="wf2-field">
          Inspect row
          <input class="wf2-control" type="number" min="1" max={file.rows.length} bind:value={rowIndex} />
        </label>
        <span class="wf2-caption">
          of {file.rows.length}. Flip through rows to see the range of values, and what each one
          becomes once the transforms run.
        </span>
      </div>
    {/if}
    <div class="wf2-tablewrap" style="overflow-x:auto">
      <table class="wf2-table">
        <thead>
          <tr>
            <th>Column</th>
            <th>In this row</th>
            <th>Shared term</th>
            <th>Identifies? · Sensitivity → Treatment</th>
            <th>Transform</th>
            <th>Published</th>
          </tr>
        </thead>
        <tbody>
          {#each groups as [record, columns]}
            <tr class="group-head">
              <td colspan="6">
                {#if datasetOf(record)}<strong>{datasetOf(record).label}</strong> <span class="mono wf2-caption">{record}</span>{:else}{record}{/if}
                <span class="group-count">{columns.length} columns</span>
              </td>
            </tr>
            {#each columns as column}
              <tr>
                <td>
                  {column.source}
                  {#if isMissing(column.source)}
                    <span class="tag warn">not in file</span>
                  {:else if profileOf(column.source)}
                    <span class="col-stats">
                      {profileOf(column.source).distinct} different{#if profileOf(column.source).blank} · {profileOf(column.source).blank} blank{/if}
                    </span>
                  {/if}
                </td>
                <td class="mono">
                  {#if !row}—{:else if raw(column).trim() === ''}<span class="wf2-caption">blank in this row</span>{:else}{raw(column)}{/if}
                </td>
                <td>
                  <button class="cell-edit" onclick={() => edit(column, 'term')}
                          aria-label="Change the shared term for {column.source}"
                  ><span class="mono">{column.term || '—'}</span>{#if column.manualTerm}<span class="wf2-caption"> by hand</span>{/if}<span class="cell-edit-suffix">edit</span></button>
                </td>
                <td>
                  <button class="cell-edit" onclick={() => edit(column, 'labels')}
                          aria-label="Change the labels for {column.source}"
                  >{column.identifies} · {column.sensitivity} → {column.treatment}{#if overridesFor(column, source.catalog).posture}<span class="tag">changed</span>{/if}{#if overridesFor(column, source.catalog).treatment}<span class="tag">forced</span>{/if}<span class="cell-edit-suffix">edit</span></button>
                </td>
                <td>
                  <button class="cell-edit" onclick={() => edit(column, 'transform')}
                          aria-label="Change the transform for {column.source}"
                  ><span class="wf2-caption">{chainLabel(column)}</span><span class="cell-edit-suffix">edit</span></button>
                </td>
                <td class="mono">{published(column) || '—'}</td>
              </tr>
            {/each}
          {/each}
        </tbody>
      </table>
    </div>
    <p class="wf2-hint">
      “Identifies?” and “Sensitivity” are your two labels; treatment is what they imply — drop, coarsen,
      isolate, or open. A column marked <span class="tag">changed</span> disagrees with the shared catalog,
      and that disagreement is saved with the mapping.
    </p>
  </Panel>

  <details class="drop-list">
    <summary>
      Not published
      <span class="group-count">{mapping.drop.length} of {mapping.drop.length + authored.length} columns</span>
    </summary>
    <Panel>
      <p class="wf2-caption">These stay in the raw record and are never published.</p>
      <div class="wf2-tablewrap">
        <table class="wf2-table">
          <tbody>
            {#each mapping.drop as header}
              <tr>
                <td>{header}</td>
                <td class="wf2-caption">
                  {#if isMissing(header)}not in the loaded file{:else}{profileOf(header)?.distinct ?? 0} different values{/if}
                </td>
                <td><Button variant="ghost" onclick={() => keepColumn(header)}>Keep and label</Button></td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </Panel>
  </details>

  <details class="drop-list">
    <summary>Record classes<span class="group-count">{records.length}</span></summary>
    <Panel>
      <p class="wf2-caption">
        A record class is here because some column's shared term belongs to it — you don't choose them
        directly. Its records are stored with the rest of its dataset's, in a container named after it.
      </p>
      <div class="wf2-tablewrap">
        <table class="wf2-table">
          <thead><tr><th>Record class</th><th>Holds</th><th>Container</th><th></th></tr></thead>
          <tbody>
            {#each records as record}
              <tr>
                <td class="mono">{record.id}</td>
                <td class="wf2-caption">
                  {#if record.columns.length}{record.columns.map((c) => c.source).join(', ')}{:else}no columns{/if}
                </td>
                <td class="mono">{containerOf(record.id)}</td>
                <td>
                  {#if !record.columns.length}
                    <Button variant="ghost" onclick={() => removeRecord(record.id)}>Remove</Button>
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </Panel>
  </details>

  <details class="drop-list">
    <summary>Added by the connector<span class="group-count">{housekeeping.length}</span></summary>
    <Panel>
      <p class="wf2-caption">
        Housekeeping written to every record — the publishing institution and the time period.
        Not yours to label.
      </p>
      <div class="wf2-tablewrap">
        <table class="wf2-table">
          <thead><tr><th>Term</th><th>From</th><th>Transform</th></tr></thead>
          <tbody>
            {#each housekeeping as column}
              <tr><td class="mono">{column.target}</td><td>{column.source}</td><td class="wf2-caption">{chainLabel(column)}</td></tr>
            {/each}
          </tbody>
        </table>
      </div>
    </Panel>
  </details>

  {#if refused}
    <Alert title={refused.message} variant="danger">
      {#if refused.reasons.length}<ul>{#each refused.reasons as reason}<li>{reason}</li>{/each}</ul>{/if}
    </Alert>
  {/if}
  {#if done}
    <Alert title="The mapping is saved" variant="success">
      <p>The records already in your Pod were read with the old mapping. The next ingest reads them again.</p>
      {#if mayVisit('source/ingest')}<Button to={`source/ingest?id=${encodeURIComponent(sourceId)}`}>Ingest</Button>{/if}
    </Alert>
  {/if}

  <div class="wf2-row" style="gap:12px">
    <Button variant="primary" onclick={save} disabled={!dirty || Boolean(blocked) || saving}>{saving ? 'Saving…' : 'Save the mapping'}</Button>
    {#if dirty}<Button variant="ghost" onclick={revert}>Undo the changes</Button>{/if}
  </div>
  {#if dirty && blocked}<p class="wf2-hint">{blocked}</p>{/if}

  <Section title="Delete this source">
    <Panel>
      {#if !removal}
        <p class="wf2-text">Removes {label}: its records, its stored file and its mapping, and the cubes
          that count only what it fills. The datasets it fills stay, ready for another source.</p>
        {#if deleteProblem}<p class="wf2-text" role="alert">{deleteProblem}</p>{/if}
        <Button variant="secondary" onclick={askDelete}>Delete {label}…</Button>
      {:else if removal.problems.length}
        <p class="wf2-text">{label} can't be deleted yet:</p>
        <ul class="wf2-text">{#each removal.problems as problem}<li>{problem}</li>{/each}</ul>
        <Button variant="ghost" onclick={() => { removal = null; }}>Close</Button>
      {:else}
        <p class="wf2-text">This removes, and can't be undone:</p>
        <ul class="wf2-text">
          <li>its records in {removal.records.map((r) => r.container).join(', ') || '—'}</li>
          <li>its stored file and its mapping</li>
          {#if removal.cubes.length}<li>{several(removal.cubes.length, 'cube')} that only it fills: {removal.cubes.join(', ')}</li>{/if}
        </ul>
        {#if removal.recounted.length}<p class="wf2-text">Other sources also fill {removal.recounted.join(', ')}, so {removal.recounted.length === 1 ? 'it is' : 'they are'} counted again without this one.</p>{/if}
        {#if removal.emptied.length}<p class="wf2-text">{removal.emptied.map((d) => d.label).join(', ')} will have no source left, and no records until you add one.</p>{/if}
        <label class="wf2-text" for="confirm-delete">Type <strong>{label}</strong> to confirm</label>
        <input id="confirm-delete" class="wf2-input" bind:value={typed} autocomplete="off" />
        {#if deleteProblem}<p class="wf2-text" role="alert">{deleteProblem}</p>{/if}
        <div class="wf2-row" style="gap:12px; margin-top: var(--space-3)">
          <Button variant="danger" onclick={confirmDelete} disabled={deleting || typed.trim() !== label}>
            {deleting ? 'Deleting…' : `Delete ${label}`}</Button>
          <Button variant="ghost" onclick={() => { removal = null; }} disabled={deleting}>Cancel</Button>
        </div>
      {/if}
    </Panel>
  </Section>
{/if}

<Drawer
  title={editing ? editing.source : ''}
  open={Boolean(editing)}
  width={620}
  onclose={() => (editing = null)}
>
  {#if editing}
    <!-- Keyed so opening a different column starts its editor fresh. -->
    {#key `${editing.source}#${facet}`}
    <ColumnEditor
      column={editing}
      {facet}
      catalog={source.catalog}
      {mapping}
      datasets={declared}
      rows={file.rows}
      headers={file.headers}
      profile={profileOf(editing.source)}
      onchange={changed}
      ondrop={() => dropColumn(editing)}
      onclose={() => (editing = null)}
    />
    {/key}
  {/if}
</Drawer>
